import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { ARTICLES, CATEGORIES } from "@/data/articles";
import { AR_ARTICLE, AR_CATEGORY, arMap } from "@/i18n_halal";
import { PublicHeader } from "@/components/PublicHeader";
import { ArrowLeft, ArrowRight, BookOpen, Clock, Languages } from "lucide-react";

const fmtDate = (d, locale) =>
  new Date(d).toLocaleDateString(locale === "ar" ? "ar-QA" : "en-GB",
    { day: "2-digit", month: "short", year: "numeric" });

/** Learn — the education hub. Explainers on halal investing, screening, zakat and global tax. */
export default function Learn() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const ar = lang === "ar";
  const [cat, setCat] = useState("All");
  const list = useMemo(
    () => (cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat)),
    [cat]
  );
  const [lead, ...rest] = list;
  const title = (a) => (ar && AR_ARTICLE[a.slug]?.title) || a.title;
  const dek = (a) => (ar && AR_ARTICLE[a.slug]?.dek) || a.dek;
  const catLabel = (c) => (ar ? arMap(AR_CATEGORY, c) : c);

  return (
    <div className="min-h-screen bg-background" data-testid="learn-page">
      <PublicHeader />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16">
        <div className="mb-8 flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="learn-back">
            <ArrowLeft className="h-4 w-4 me-1.5" />{t("hl_back")}
          </Button>
        </div>

        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" />{t("ln_eyebrow")}
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="learn-title">
          {t("ln_h1a")}<br />
          <span className="text-[color:var(--tharwy-indigo)]">{t("ln_h1b")}</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base text-muted-foreground leading-relaxed">{t("ln_sub")}</p>

        {ar && (
          <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-[11px] text-muted-foreground"
            data-testid="learn-english-note">
            <Languages className="h-3.5 w-3.5" />{t("ln_english_note")}
          </p>
        )}

        <div className="mt-9 flex flex-wrap gap-2" data-testid="learn-categories">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                cat === c ? "bg-[#3E2E65] text-white border-[#3E2E65]"
                          : "bg-transparent border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`learn-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}>
              {catLabel(c)}
            </button>
          ))}
        </div>

        {lead && (
          <button onClick={() => navigate(`/learn/${lead.slug}`)}
            className="mt-8 w-full text-start rounded-3xl navy-strip text-white p-7 sm:p-10 relative overflow-hidden group"
            data-testid={`learn-lead-${lead.slug}`}>
            <div className="absolute inset-0 hero-sheen opacity-50" />
            <div className="relative max-w-2xl">
              <span className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">{catLabel(lead.category)}</span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">{title(lead)}</h2>
              <p className="mt-3 text-white/70 leading-relaxed">{dek(lead)}</p>
              <div className="mt-6 flex items-center gap-4 text-xs text-white/50">
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{lead.read} {t("ln_min_read")}</span>
                <span>{fmtDate(lead.date, lang)}</span>
                <span className="flex items-center gap-1 text-[color:var(--tharwy-lime)] group-hover:gap-2 transition-all">
                  {t("ln_read")} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </div>
          </button>
        )}

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="learn-grid">
          {rest.map((a) => (
            <button key={a.slug} onClick={() => navigate(`/learn/${a.slug}`)}
              className="text-start rounded-2xl border border-border bg-card p-6 transition-shadow hover:shadow-premium group"
              data-testid={`learn-card-${a.slug}`}>
              <span className="text-[11px] uppercase tracking-[0.14em] text-[#3E2E65]">{catLabel(a.category)}</span>
              <h3 className="mt-2.5 text-base md:text-lg font-semibold leading-snug">{title(a)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{dek(a)}</p>
              <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{a.read} {t("ln_min")}</span>
                <span className="flex items-center gap-1 text-[#3E2E65] group-hover:gap-2 transition-all">
                  {t("ln_read")} <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-14 rounded-3xl border border-border bg-card p-8 sm:p-10 text-center">
          <h2 className="text-base md:text-lg font-semibold">{t("ln_cta_title")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("ln_cta_sub")}</p>
          <Button className="mt-5 rounded-full px-7" onClick={() => navigate("/login")} data-testid="learn-cta">
            {t("ln_cta_button")} <ArrowRight className="h-4 w-4 ms-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
